'use client';
'use client';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

// Animated counter hook
function useAnimatedCounter(end: number, duration: number = 2000, delay: number = 0) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let start = 0;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [end, duration, delay]);

  return count;
}

// Donut chart component
function BudgetDonut({ used, total }: { used: number; total: number }) {
  const percentage = (used / total) * 100;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative w-24 h-24">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
          style={{ animationDelay: '0.5s' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-foreground">{Math.round(percentage)}%</span>
        <span className="text-[9px] text-muted-foreground">utilized</span>
      </div>
    </div>
  );
}

// Vendor match card
function VendorCard({
  name,
  category,
  matchScore,
  avatar,
  delay,
  position,
}: {
  name: string;
  category: string;
  matchScore: number;
  avatar: string;
  delay: number;
  position: 'top-right' | 'bottom-left' | 'right';
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const positionClasses = {
    'top-right': 'top-[2%] right-[0%] sm:top-[5%] sm:right-[-5%]',
    'bottom-left': 'bottom-[15%] left-[-5%] sm:bottom-[18%] sm:left-[-12%]',
    'right': 'top-[45%] right-[-8%] sm:right-[-15%]',
  };

  return (
    <div
      className={`absolute ${positionClasses[position]} transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
    >
      <div
        className="px-3 py-2.5 rounded-xl flex items-center gap-2.5 animate-float"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.92) 100%)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          border: '1px solid rgba(255,255,255,0.8)',
          animationDelay: `${delay / 1000}s`,
        }}
      >
        {/* Avatar */}
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${avatar} 0%, ${avatar}dd 100%)`,
          }}
        >
          {name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-foreground truncate">{name}</div>
          <div className="text-[10px] text-muted-foreground">{category}</div>
        </div>
        {/* Match score */}
        <div className="flex flex-col items-end">
          <div className="text-xs font-bold text-emerald-600">{matchScore}%</div>
          <div className="text-[8px] text-muted-foreground">match</div>
        </div>
      </div>
    </div>
  );
}

// WhatsApp message bubble
function WhatsAppBubble({ delay }: { delay: number }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`absolute bottom-[5%] right-[5%] sm:bottom-[8%] sm:right-[2%] transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
    >
      <div
        className="p-3 rounded-2xl rounded-br-sm max-w-[180px] animate-float-delayed"
        style={{
          background: '#DCF8C6',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          animationDelay: '1.5s',
        }}
      >
        <div className="flex items-center gap-1.5 mb-1.5">
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[#25D366]" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="text-[10px] font-semibold text-[#075E54]">Tarboot</span>
        </div>
        <p className="text-[11px] text-gray-800 leading-relaxed">
          Found 3 caterers matching your criteria. <span className="font-medium">Top pick: Gourmet Events</span> - 98% match! 🎯
        </p>
        <div className="text-[9px] text-gray-500 text-right mt-1">Just now</div>
      </div>
    </div>
  );
}

// Document status indicator
function DocStatus({ label, status, delay }: { label: string; status: 'complete' | 'pending' | 'missing'; delay: number }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const statusConfig = {
    complete: { color: '#10B981', bg: '#D1FAE5', icon: '✓' },
    pending: { color: '#F59E0B', bg: '#FEF3C7', icon: '⏳' },
    missing: { color: '#EF4444', bg: '#FEE2E2', icon: '!' },
  };

  const config = statusConfig[status];

  return (
    <div
      className={`flex items-center gap-2 transition-all duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'
        }`}
    >
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold"
        style={{ background: config.bg, color: config.color }}
      >
        {config.icon}
      </div>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

export function HeroIllustration() {
  const [isVisible, setIsVisible] = useState(false);
  const tIll = useTranslations('landing.illustration');

  const budgetUsed = useAnimatedCounter(34250, 1500, 800);
  const budgetTotal = 50000;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`relative w-[340px] h-[380px] sm:w-[440px] sm:h-[480px] lg:w-[520px] lg:h-[560px] transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
    >
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[500px] sm:h-[500px]"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 50% 50%,
              rgba(198, 93, 59, 0.12) 0%,
              rgba(198, 93, 59, 0.04) 50%,
              transparent 80%
            )
          `,
          filter: 'blur(40px)',
        }}
      />

      {/* Main dashboard card */}
      <div
        className={`relative mx-auto mt-8 w-[280px] sm:w-[340px] lg:w-[380px] rounded-2xl overflow-hidden transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.94) 100%)',
          boxShadow: `
            0 25px 50px -12px rgba(0,0,0,0.12),
            0 12px 24px -8px rgba(0,0,0,0.08),
            0 0 0 1px rgba(0,0,0,0.03),
            inset 0 1px 0 rgba(255,255,255,0.9)
          `,
        }}
      >
        {/* Card header */}
        <div className="px-5 py-4 border-b border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Tech Summit 2024</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Oct 15 • Tel Aviv Convention Center</p>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-medium text-emerald-700">On Track</span>
            </div>
          </div>
        </div>

        {/* Card content */}
        <div className="p-5">
          {/* Budget section */}
          <div className="flex items-center gap-5">
            <BudgetDonut used={budgetUsed} total={budgetTotal} />
            <div className="flex-1">
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Budget</div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Spent</span>
                  <span className="text-xs font-semibold text-foreground">₪{budgetUsed.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Remaining</span>
                  <span className="text-xs font-semibold text-emerald-600">₪{(budgetTotal - budgetUsed).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">Total</span>
                  <span className="text-xs font-bold text-foreground">₪{budgetTotal.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-4 border-t border-border/50" />

          {/* Documents section */}
          <div>
            <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-3">Documents</div>
            <div className="grid grid-cols-2 gap-2">
              <DocStatus label={tIll('venueContract')} status="complete" delay={1000} />
              <DocStatus label="Insurance" status="complete" delay={1100} />
              <DocStatus label={tIll('cateringQuote')} status="pending" delay={1200} />
              <DocStatus label="AV Invoice" status="missing" delay={1300} />
            </div>
          </div>

          {/* Divider */}
          <div className="my-4 border-t border-border/50" />

          {/* Vendor progress */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Vendors Confirmed</span>
              <span className="text-xs font-semibold text-foreground">4/6</span>
            </div>
            <div className="flex gap-1.5">
              {['#C65D3B', '#7C3AED', '#0EA5E9', '#10B981', '#D1D5DB', '#D1D5DB'].map((color, i) => (
                <div
                  key={i}
                  className="h-1.5 flex-1 rounded-full transition-all duration-500"
                  style={{
                    background: color,
                    opacity: color === '#D1D5DB' ? 0.5 : 1,
                    transitionDelay: `${1400 + i * 100}ms`,
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[9px] text-muted-foreground">
              <span>{tIll('catering')}</span>
              <span>{tIll('venue')}</span>
              <span>AV</span>
              <span>{tIll('decor')}</span>
              <span>{tIll('photo')}</span>
              <span>{tIll('security')}</span>
            </div>
          </div>
        </div>

        {/* Shimmer effect */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
            animationDelay: '2s',
          }}
        />
      </div>

      {/* Floating vendor cards */}
      <VendorCard
        name="Gourmet Events"
        category={tIll('catering')}
        matchScore={98}
        avatar="#C65D3B"
        delay={800}
        position="top-right"
      />
      <VendorCard
        name="SoundWave Pro"
        category="Audio/Visual"
        matchScore={94}
        avatar="#0EA5E9"
        delay={1000}
        position="right"
      />
      <VendorCard
        name="Bloom & Co"
        category={tIll('decorFlorals')}
        matchScore={91}
        avatar="#10B981"
        delay={1200}
        position="bottom-left"
      />

      {/* WhatsApp bubble */}
      <WhatsAppBubble delay={1600} />

      {/* AI matching indicator */}
      <div
        className={`absolute top-[12%] left-[2%] sm:left-[-2%] transition-all duration-700 delay-[1400ms] ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
          }`}
      >
        <div
          className="px-3 py-2 rounded-xl animate-float"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(24 70% 55%) 100%)',
            boxShadow: '0 4px 16px rgba(198, 93, 59, 0.4)',
            animationDelay: '0.8s',
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <div>
              <div className="text-[10px] font-semibold">AI Matching</div>
              <div className="text-[8px] opacity-80">Finding vendors...</div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-[5%] left-[20%] w-2 h-2 rounded-full bg-primary/40 animate-pulse" />
      <div className="absolute bottom-[20%] right-[25%] w-1.5 h-1.5 rounded-full bg-violet-400/50 animate-pulse" style={{ animationDelay: '0.5s' }} />
      <div className="absolute top-[40%] left-[5%] w-2.5 h-2.5 rounded-full bg-emerald-400/40 animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Inline keyframes */}
      <style>{`
        @keyframes shimmer {
          0%, 100% {
            transform: translateX(-100%);
            opacity: 0;
          }
          50% {
            transform: translateX(100%);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
